<script>
import axios from 'axios'
import { mapMutations, mapGetters } from 'vuex'
import BookingSummary from '@components/booking-summary'
import GuestInformationForm from '@components/guest-information-form'
import PaymentInformationForm from '@components/payment-information-form'
import NotificationModal from '@components/notification-modal'
import { bookingPageTracking, terminateTracking } from '@src/tracking'

export default {
  page() {
    return {
      title: `${this.property.name} - Booking`,
      meta: [
        {
          name: 'description',
          content: 'Book easier with many benefit with direct booking',
        },
      ],
    }
  },

  components: {
    BookingSummary,
    GuestInformationForm,
    PaymentInformationForm,
    NotificationModal
},

  props: {
    dropdown: { type: Object, required: true },
    property: { type: Object, required: true },
  },

  data() {
    return {
      loading: false,
      showNotificationModal: false,
      error: {
        title: '',
        message: '',
      },
    }
  },

  computed: {
    ...mapGetters('cart', { cartPayload: 'payload' }),
    ...mapGetters('cart', ['transactionDetails', 'trackingParams']),
    payload() {
      return {
        propertyId: this.property.id,
        cart: this.cartPayload,
        transactionDetails: this.transactionDetails,
        guest: this.$refs.guestForm.$data.form,
        payment: this.$refs.paymentForm.$data.form,
      }
    },
  },

  mounted() {
    if (process.env.NODE_ENV === 'production') {
      bookingPageTracking(this.trackingParams, this.property)
    }
  },

  beforeDestroy() {
    if (process.env.NODE_ENV === 'production') {
      terminateTracking()
    }
  },

  methods: {
    ...mapMutations('app', {
      toggleTerms: 'TOGGLE_TERMS',
      togglePrivacy: 'TOGGLE_PRIVACY',
    }),
    showTermsModal() {
      this.toggleTerms(true)
    },
    showPrivacyModal() {
      this.togglePrivacy(true)
    },
    submit() {
      // uncomment code bellow for testing
      // this.$router.push({
      //   name: 'booking-confirmation',
      //   params: { bookingId: 1 },
      // })

      this.loading = true
      axios
        .post('/api/v2/book', this.payload)
        .then((res) => {
          Object.assign(this.error, { title:'', message: '' })
          this.loading = false

          // push router to confirmation booking with id
          const { id } = res.data
          this.$router.push({
            name: 'booking-confirmation',
            params: { bookingId: id },
            query: this.$route.query,
          })
        })
        .catch((err) => {
          const { data } = err.response

          if(data) {
            this.error.title = data.title || '';
            this.error.message = data.message || '';
            this.showNotificationModal = true
          }

          this.loading = false
        })
    },
  },
}
</script>

<template>
  <section class="mt-6">
    <div class="container container-page mx-auto px-6">
      <div class="flex flex-wrap-reverse lg:flex-wrap -mx-3">
        <div class="w-full lg:w-2/3 px-3 mb-6">
          <ValidationObserver v-slot="{ invalid, passes }" slim>
            <form @submit.prevent="passes(submit)">
              <BaseBox title="Guest information" class="mb-6">
                <GuestInformationForm ref="guestForm" :dropdown="dropdown" />
              </BaseBox>
              <BaseBox title="Payment" class="mb-6">
                <PaymentInformationForm
                  ref="paymentForm"
                  :dropdown="dropdown"
                  :invalid="invalid"
                />
              </BaseBox>
              <p class="text-sm italic mb-6">
                By clicking the confirm reservation button below to submit this
                booking, I agree to {{ property.name }}
                <a class="text-gold-500 cursor-pointer" @click="showTermsModal">
                  Terms of Use
                </a>
                and
                <a
                  class="text-gold-500 cursor-pointer"
                  @click="showPrivacyModal"
                >
                  Privacy Policy
                </a>
                .
              </p>
              <div class="text-right">
                <button
                  type="submit"
                  :class="['btn', { 'btn-disabled': invalid }, 'btn-gold']"
                  :disabled="invalid"
                >
                  Confirm Reservation
                </button>
              </div>
            </form>
          </ValidationObserver>
        </div>
        <div class="w-full lg:w-1/3 px-3 mb-6">
          <BookingSummary
            :maximum-infant-age="property.policy.maximumInfantAge"
          />
        </div>
      </div>
    </div>

    <NotificationModal v-model="showNotificationModal" :title="error.title" :message="error.message" />
  </section>
</template>
